import { useEffect, useState } from "react";

import { OrganizationEndpoints } from "@generated/organization";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import type {
  TagSchemaDefinition,
  Team,
} from "../../../../organization/types/OrganizationTypes";

export interface OrgTagSchemaState {
  tagSchema: TagSchemaDefinition[];
  tagValues: Record<string, string>;
  teams: Team[];
  setTagValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

/**
 * Fetches the FinOps tag schema and teams for an organization.
 * Pre-fills default tag values from the schema definition.
 */
export function useOrgTagSchema(
  orgAlias: string | undefined,
): OrgTagSchemaState {
  const [tagSchema, setTagSchema] = useState<TagSchemaDefinition[]>([]);
  const [tagValues, setTagValues] = useState<Record<string, string>>({});
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    if (!orgAlias) return;

    fetch(
      `${apiBaseUrls.BACKEND}${OrganizationEndpoints.GET_TAG_SCHEMA.replace(":alias", orgAlias)}`,
      { credentials: "include" },
    )
      .then((r) => r.json())
      .then((data: TagSchemaDefinition[]) => {
        setTagSchema(data);
        // Pre-fill default values
        const defaults: Record<string, string> = {};
        for (const def of data) {
          if (def.defaultValue) defaults[def.key] = def.defaultValue;
        }
        setTagValues(defaults);
      })
      .catch(() => setTagSchema([]));

    fetch(
      `${apiBaseUrls.BACKEND}${OrganizationEndpoints.LIST_TEAMS.replace(":alias", orgAlias)}`,
      { credentials: "include" },
    )
      .then((r) => r.json())
      .then((data: Team[]) => setTeams(data))
      .catch(() => setTeams([]));
  }, [orgAlias]);

  return { tagSchema, tagValues, teams, setTagValues };
}
